import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticPump } from "./realistic";

/**
 * PumpNode – Centrifugal pump visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with rotating volute impeller & fluid glow
 * - Premium glassmorphism design switch
 * - Classic industrial SVG layout
 */
const PumpNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticPump
              liveValue={liveValue}
              unit={unit || "RPM"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const spinDuration = isRunning ? `${2 / (speedScale || 1)}s` : "0s";

        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="88"
            viewBox="0 0 80 88"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="pumpGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="pumpActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.6" />
                  </linearGradient>
                  <filter id="pumpGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Outer premium shadow ring */}
                <circle
                  cx="40"
                  cy="40"
                  r="32"
                  fill="url(#pumpGlassGrad)"
                  stroke={isRunning ? "url(#pumpActiveGrad)" : "#475569"}
                  strokeWidth="3"
                  style={{
                    filter: isRunning ? "url(#pumpGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Swirling liquid flow ring */}
                {isRunning && (
                  <circle
                    cx="40"
                    cy="40"
                    r="24"
                    fill="none"
                    stroke={color}
                    strokeWidth="3.5"
                    strokeDasharray="15 25"
                    opacity="0.6"
                    filter="url(#pumpGlow)"
                  >
                    <animate attributeName="stroke-dashoffset" values="0;40" dur="0.8s" repeatCount="indefinite" />
                  </circle>
                )}

                {/* Animated impeller blades (curved and thicker) */}
                <g
                  style={{
                    transformOrigin: "40px 40px",
                    animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none",
                  }}
                >
                  <path d="M40 12 Q45 28 40 40 Q55 45 60 20" stroke={isRunning ? color : "#64748b"} strokeWidth="4.5" strokeLinecap="round" opacity="0.9" />
                  <path d="M40 68 Q35 52 40 40 Q25 35 20 60" stroke={isRunning ? color : "#64748b"} strokeWidth="4.5" strokeLinecap="round" opacity="0.9" />
                  <path d="M12 40 Q28 35 40 40 Q35 25 20 20" stroke={isRunning ? color : "#64748b"} strokeWidth="4.5" strokeLinecap="round" opacity="0.9" />
                  <path d="M68 40 Q52 45 40 40 Q45 55 60 60" stroke={isRunning ? color : "#64748b"} strokeWidth="4.5" strokeLinecap="round" opacity="0.9" />
                </g>

                {/* Glowing Center Hub */}
                <circle
                  cx="40"
                  cy="40"
                  r="7"
                  fill={isRunning ? "url(#pumpActiveGrad)" : "#64748b"}
                  style={{ filter: isRunning ? "url(#pumpGlow)" : "none" }}
                />

                {/* Glassy Overlay Status Ring */}
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke={dotColor}
                  strokeWidth="2"
                  strokeDasharray="6 3"
                  opacity="0.8"
                  style={{
                    animation: isRunning ? "live-motor-pulse 2s ease-in-out infinite" : "none",
                  }}
                />

                {/* Premium status indicator dot */}
                <circle
                  cx="68"
                  cy="12"
                  r="6"
                  fill={dotColor}
                  stroke="#ffffff"
                  strokeWidth="2"
                  filter={isRunning ? "url(#pumpGlow)" : "none"}
                  style={{ filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.3))" }}
                >
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Pump body */}
                <circle
                  cx="40"
                  cy="40"
                  r="32"
                  fill={isRunning ? `${color}20` : `${color}08`}
                  stroke={strokeColor}
                  strokeWidth="2.5"
                />

                {/* Impeller group */}
                <g
                  style={{
                    transformOrigin: "40px 40px",
                    animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none",
                  }}
                >
                  <path d="M40 12 L40 40 L60 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
                  <path d="M40 68 L40 40 L20 60" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
                  <path d="M12 40 L40 40 L20 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
                  <path d="M68 40 L40 40 L60 60" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
                </g>

                {/* Center hub */}
                <circle cx="40" cy="40" r="6" fill={isRunning ? color : "#94a3b8"} />

                {/* Status indicator ring */}
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke={dotColor}
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  opacity="0.5"
                />

                {/* Inlet pipe indicator */}
                <rect x="0" y="35" width="8" height="10" fill={strokeColor} rx="2" />

                {/* Outlet pipe indicator */}
                <rect x="35" y="0" width="10" height="8" fill={strokeColor} rx="2" />

                {/* Status dot */}
                <circle cx="68" cy="12" r="5" fill={dotColor} stroke="#fff" strokeWidth="1.5" />
              </>
            )}

            {/* Status text - clearly below pump circle */}
            <text x="40" y="84" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

PumpNode.propTypes = {
  data: PropTypes.object,
  selected: PropTypes.bool,
  id: PropTypes.string,
};

export default PumpNode;
